/**
 * Email Templates API Service
 * Manages email template operations
 */

import api from './api';

// TypeScript types matching backend models
export interface TemplateVariable {
  name: string;
  description: string;
  required: boolean;
  exampleValue?: string;
}

export interface EmailTemplate {
  id: string;
  templateType: string;
  name: string;
  description: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  variables: TemplateVariable[];
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
}

export interface CreateEmailTemplateRequest {
  templateType: string;
  name: string;
  description: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  variables: TemplateVariable[];
}

export interface PreviewEmailResult {
  subject: string;
  htmlBody: string;
  textBody?: string;
}

export interface SendTestEmailRequest {
  recipientEmail: string;
  testVariables: Record<string, any>;
}

export interface UpdateEmailTemplateRequest {
  name: string;
  description: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  variables: TemplateVariable[];
  isActive: boolean;
}

class EmailService {
  private baseUrl = '/email';

  /**
   * List all email templates
   */
  async listTemplates(): Promise<EmailTemplate[]> {
    const response = await api.get(`${this.baseUrl}/templates`);
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Failed to fetch email templates (${response.status}): ${errorText || response.statusText}`);
    }
    const data = await response.json();
    return data || [];
  }

  /**
   * Get email template by ID
   */
  async getTemplate(id: string): Promise<EmailTemplate> {
    const response = await api.get(`${this.baseUrl}/templates/${id}`);
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Email template not found');
      }
      throw new Error('Failed to fetch email template');
    }
    return response.json();
  }

  /**
   * Create a new email template
   */
  async createTemplate(request: CreateEmailTemplateRequest): Promise<EmailTemplate> {
    const response = await api.post(`${this.baseUrl}/templates`, request);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to create email template');
    }
    return response.json();
  }

  /**
   * Preview email template with sample data
   */
  async previewTemplate(id: string, sampleData: Record<string, any>): Promise<PreviewEmailResult> {
    const response = await api.post(`${this.baseUrl}/templates/${id}/preview`, sampleData);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to preview email template');
    }
    return response.json();
  }

  /**
   * Send test email
   */
  async sendTestEmail(id: string, request: SendTestEmailRequest): Promise<void> {
    const response = await api.post(`${this.baseUrl}/templates/${id}/test`, request);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to send test email');
    }
  }

  /**
   * Update email template
   */
  async updateTemplate(id: string, request: UpdateEmailTemplateRequest): Promise<EmailTemplate> {
    const response = await api.put(`${this.baseUrl}/templates/${id}`, request);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to update email template');
    }
    return response.json();
  }

  /**
   * Delete email template
   */
  async deleteTemplate(id: string): Promise<void> {
    const response = await api.delete(`${this.baseUrl}/templates/${id}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to delete email template');
    }
  }

  /**
   * Get badge color for template type (based on string matching)
   */
  getTemplateTypeBadgeColor(templateType: string): string {
    const lowerType = templateType.toLowerCase();

    if (lowerType.includes('invite')) return 'primary';
    if (lowerType.includes('password') && lowerType.includes('reset')) return 'warning';
    if (lowerType.includes('password') && lowerType.includes('changed')) return 'info';
    if (lowerType.includes('complete') || lowerType.includes('success')) return 'success';
    if (lowerType.includes('failed') || lowerType.includes('error')) return 'danger';
    if (lowerType.includes('welcome')) return 'success';
    if (lowerType.includes('expir')) return 'warning';

    return 'secondary';
  }
}

export const emailService = new EmailService();
